import { ChangeDetectorRef, DestroyRef, EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs';

/**
 * Base interface for service detail items that can be tracked by the form tracker
 */
export interface ServiceDetailItem {
  id: string;
  basePrice: number;
}

/**
 * Field mapping configuration for matching form fields to item properties
 */
export interface FieldMapping {
  formField: string;
  itemField: string;
  transform?: (value: any) => any; // Optional transformation function
}

/**
 * Generic configuration object for service form tracking
 */
export interface ServiceFormTrackingConfig<T extends ServiceDetailItem> {
  form: FormGroup;
  items: T[];
  validationFields: string[]; // Fields that must be valid for the form to be considered valid
  matchingFields: FieldMapping[]; // Fields used to match items
  controlNames: {
    id: string;
    totalPrice: string;
  };
  formStatusChange: EventEmitter<boolean>;
  destroyRef: DestroyRef;
  changeDetectorRef?: ChangeDetectorRef;
}

/**
 * Utility class for tracking service form changes and updating related controls
 */
export class ServiceFormTracker {
  /**
   * Tracks form changes and automatically update ID and total price controls
   * based on matching service items
   */
  static trackServiceForm<T extends ServiceDetailItem>(config: ServiceFormTrackingConfig<T>): void {
    const {
      form,
      items,
      validationFields,
      matchingFields,
      controlNames,
      formStatusChange,
      destroyRef,
      changeDetectorRef,
    } = config;
    const totalPriceControl = form.get(controlNames.totalPrice);

    // Get all required form controls for validation
    const validationControls = validationFields
      .map((fieldName) => {
        const control = form.get(fieldName);
        if (!control) {
          console.warn(`ServiceFormTracker: Required form control '${fieldName}' not found`);
        }
        return { fieldName, control };
      })
      .filter((item) => item.control !== null);

    if (validationControls.length === 0) {
      console.warn('ServiceFormTracker: No valid form controls found for validation');
      return;
    }

    // Create observables for all validation controls
    const validationStreams = validationControls.map(({ control }) =>
      control!.valueChanges.pipe(startWith(control!.value)),
    );

    // Create a computed observable for form data
    const formData$ = combineLatest(validationStreams).pipe(
      map(() => {
        // Check if all validation controls are valid
        const isValid = validationControls.every(({ control }) => control!.valid || control!.errors === null);

        // Get current form values for matching
        const formValues = form.getRawValue();

        return { isValid, formValues };
      }),
      shareReplay(1),
    );

    // Stream 1: Form validation (immediate feedback, no debounce)
    formData$
      .pipe(
        takeUntilDestroyed(destroyRef),
        map((data) => data.isValid),
        distinctUntilChanged(), // Only emit when validation status changes
      )
      .subscribe((isValid) => formStatusChange.emit(isValid));

    // Stream 2: Control updates (debounced, only when valid)
    formData$
      .pipe(
        takeUntilDestroyed(destroyRef),
        debounceTime(300), // Debounce only the updates, not validation
        map(({ formValues }) => {
          // Find matching item based on configured matching fields
          return items.find((item) => {
            return matchingFields.every((mapping) => {
              const formValue = formValues[mapping.formField];
              const transformedValue = mapping.transform ? mapping.transform(formValue) : formValue;
              const itemValue = (item as any)[mapping.itemField];
              return transformedValue === itemValue;
            });
          });
        }),
        filter((item) => !!item), // Only proceed if the item is found
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id), // Only update if different item
      )
      .subscribe((selectedItem: T) => {
        console.log(selectedItem);
        // Use patchValue with emitEvent: false to prevent infinite loops
        form.patchValue(
          {
            [controlNames.id]: selectedItem.id,
            [controlNames.totalPrice]: selectedItem.basePrice,
          },
          { emitEvent: false },
        );
        totalPriceControl?.updateValueAndValidity();
        changeDetectorRef?.markForCheck();
      });
  }
}
